import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../utils/axios';
import { resolveModuleKey } from '../utils/platformModuleRouteMap';
import { resolveCompanionScene } from '../components/Companion/guideScenes';
import { companionNavigate } from '../components/Companion/companionNavigate';
import { MAX_PENDING_IMAGES, filesToPendingImages } from '../components/Companion/companionChatAttachments';

const WaySenCompanionContext = createContext(null);

const LS_GEOM = 'mb_companion_window_geometry';
const LS_ACTIVE_MODE = 'mb_companion_active_mode';
const CACHE_SCENES_TTL_MS = 5 * 60 * 1000;
const CACHE_HEALTH_TTL_MS = 30 * 1000;
const COMPANION_MODES_SET = new Set(['talk', 'guide', 'support', 'do', 'tasks']);

const COMPANION_ENABLED =
  String(process.env.REACT_APP_COMPANION_ENABLED || 'true').toLowerCase() !== 'false';

function loadGeom() {
  try {
    return JSON.parse(localStorage.getItem(LS_GEOM) || '{}');
  } catch {
    return {};
  }
}

function saveGeom(geom) {
  try {
    localStorage.setItem(LS_GEOM, JSON.stringify(geom || {}));
  } catch { /* ignore */ }
}

function loadActiveMode() {
  try {
    const mode = localStorage.getItem(LS_ACTIVE_MODE);
    if (COMPANION_MODES_SET.has(mode)) return mode;
  } catch { /* ignore */ }
  return 'guide';
}

function saveActiveMode(mode) {
  try {
    if (COMPANION_MODES_SET.has(mode)) {
      localStorage.setItem(LS_ACTIVE_MODE, mode);
    }
  } catch { /* ignore */ }
}

/** Desktop + mobile: open as floating window (mobile CSS makes it full-bleed). */
function resolveOpenPanelState(asWindow) {
  if (asWindow === false) return 'expanded';
  return 'window';
}

export function WaySenCompanionProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth?.user);
  const company = useSelector((s) => s.auth?.company);
  const isAuthenticated = useSelector((s) => s.auth?.isAuthenticated);

  const [panelState, setPanelState] = useState('minimized'); // minimized | expanded | window | hidden
  const [activeMode, setActiveModeState] = useState(loadActiveMode); // talk | guide | support | do
  const setActiveMode = useCallback((mode) => {
    const next = COMPANION_MODES_SET.has(mode) ? mode : 'guide';
    setActiveModeState(next);
    saveActiveMode(next);
  }, []);
  const [avatarState, setAvatarState] = useState('idle');
  const [state, setState] = useState({
    visibility: 'auto',
    gender: 'm',
    only_on_demand: false,
    window_size_preset: 'md',
    dismissed_scene_ids: [],
    completed_flow_ids: [],
    window_geometry: loadGeom(),
    enabled: COMPANION_ENABLED,
  });
  const [activeFlowBlock, setActiveFlowBlock] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [activeScene, setActiveScene] = useState(null);
  const [setupHealth, setSetupHealth] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [activeFormId, setActiveFormId] = useState(null);
  const [formSchema, setFormSchema] = useState(null);
  const [formPrefill, setFormPrefill] = useState(null);
  const [degraded, setDegraded] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [chatDraft, setChatDraft] = useState('');
  const [chatPendingImages, setChatPendingImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [operatorConsent, setOperatorConsent] = useState(null);
  const [operatorConsentLoading, setOperatorConsentLoading] = useState(false);
  const [operatorActions, setOperatorActions] = useState([]);
  const [operatorActionsLoading, setOperatorActionsLoading] = useState(false);

  const scenesCacheRef = useRef({ at: 0, data: [] });
  const healthCacheRef = useRef({ at: 0, companyId: null, data: null });
  const proactiveRef = useRef({ day: '', count: 0 });
  const chatRequestRef = useRef(null);
  const chatSendSequenceRef = useRef(0);
  const chatAvatarTimerRef = useRef(null);
  const chatMountedRef = useRef(true);
  const chatScopeRef = useRef({ companyId: null, userId: null, authenticated: null, generation: 0 });

  const companyId = company?.id || null;
  const userId = user?.id || null;
  // Invalidate at render time too: a previous company's response may settle before effects run.
  if (chatScopeRef.current.companyId !== companyId
    || chatScopeRef.current.userId !== userId
    || chatScopeRef.current.authenticated !== isAuthenticated) {
    chatScopeRef.current = { companyId, userId, authenticated: isAuthenticated, generation: chatScopeRef.current.generation + 1 };
    chatRequestRef.current = null;
  }
  const chatScopeKey = chatScopeRef.current.generation;
  const moduleKey = resolveModuleKey(location.pathname);

  useLayoutEffect(() => {
    setMessages([]);
    setSessionId(null);
    setChatError(null);
    setChatDraft('');
    setChatPendingImages([]);
    setLoading(false);
    setAvatarState('idle');
    clearTimeout(chatAvatarTimerRef.current);
  }, [chatScopeKey]);

  useEffect(() => {
    chatMountedRef.current = true;
    return () => {
      chatMountedRef.current = false;
      chatRequestRef.current = null;
      clearTimeout(chatAvatarTimerRef.current);
    };
  }, []);

  // File encoding may finish after its composer unmounts. Only this owner can
  // accept the result, and only for the same company/user/auth generation.
  const addChatFiles = useCallback(async (files) => {
    const generation = chatScopeRef.current.generation;
    const sendSequence = chatSendSequenceRef.current;
    const images = await filesToPendingImages(files);
    if (!chatMountedRef.current || chatScopeRef.current.generation !== generation
      || chatSendSequenceRef.current !== sendSequence || chatRequestRef.current) return;
    const next = images.filter((src) => typeof src === 'string' && src.startsWith('data:image/'));
    if (!next.length) return;
    setChatPendingImages((prev) => [...prev, ...next].slice(0, MAX_PENDING_IMAGES));
  }, []);

  const visible = useMemo(() => {
    if (!COMPANION_ENABLED || !isAuthenticated || !user) return false;
    if (state.visibility === 'off') return false;
    return true;
  }, [isAuthenticated, user, state.visibility]);

  const invalidateCaches = useCallback(() => {
    scenesCacheRef.current = { at: 0, data: [] };
    healthCacheRef.current = { at: 0, companyId: null, data: null };
  }, []);

  const refreshOperatorConsent = useCallback(async () => {
    if (!companyId || !isAuthenticated) {
      setOperatorConsent(null);
      return null;
    }
    setOperatorConsentLoading(true);
    try {
      const { data } = await api.get('/api/companion/operator-consent');
      const status = data?.data || { accepted: false };
      setOperatorConsent(status);
      return status;
    } catch {
      // Fail-closed for Do: show gate with empty texts fallback
      setOperatorConsent({
        accepted: false,
        version: 'operator_ai_v1',
        texts: {},
      });
      return null;
    } finally {
      setOperatorConsentLoading(false);
    }
  }, [companyId, isAuthenticated]);

  const acceptOperatorConsent = useCallback(async (payload) => {
    try {
      const { data } = await api.post('/api/companion/operator-consent', {
        consent_ai_processing: payload?.consent_ai_processing === true,
        disclaimer_user_responsibility: payload?.disclaimer_user_responsibility === true,
      });
      if (data?.success === false) {
        return { error: data?.error || 'Не удалось сохранить согласие' };
      }
      const status = data?.data || { accepted: true };
      setOperatorConsent(status);
      return status;
    } catch (e) {
      return {
        error: e?.response?.data?.error || 'Не удалось сохранить согласие',
        code: e?.response?.data?.code,
      };
    }
  }, []);

  const refreshOperatorActions = useCallback(async () => {
    if (!companyId || !isAuthenticated) {
      setOperatorActions([]);
      return [];
    }
    setOperatorActionsLoading(true);
    try {
      const { data } = await api.get('/api/companion/operator-actions');
      const list = Array.isArray(data?.data?.actions) ? data.data.actions : [];
      setOperatorActions(list);
      return list;
    } catch {
      setOperatorActions([]);
      return [];
    } finally {
      setOperatorActionsLoading(false);
    }
  }, [companyId, isAuthenticated]);

  const fetchState = useCallback(async () => {
    try {
      const { data } = await api.get('/api/companion/state');
      if (data?.data) {
        setState((prev) => ({
          ...prev,
          ...data.data,
          window_geometry: data.data.window_geometry?.x != null
            ? data.data.window_geometry
            : (prev.window_geometry || loadGeom()),
        }));
      }
    } catch {
      setDegraded(true);
    }
  }, []);

  const fetchScenes = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && scenesCacheRef.current.at && now - scenesCacheRef.current.at < CACHE_SCENES_TTL_MS) {
      setScenes(scenesCacheRef.current.data);
      return scenesCacheRef.current.data;
    }
    try {
      const { data } = await api.get('/api/companion/scenes');
      const list = data?.data?.scenes || [];
      scenesCacheRef.current = { at: now, data: list };
      setScenes(list);
      setDegraded(Boolean(data?.data?.degraded));
      return list;
    } catch {
      setDegraded(true);
      return scenesCacheRef.current.data || [];
    }
  }, []);

  const fetchHealth = useCallback(async (force = false) => {
    if (!companyId) return null;
    const now = Date.now();
    if (
      !force
      && healthCacheRef.current.companyId === companyId
      && healthCacheRef.current.at
      && now - healthCacheRef.current.at < CACHE_HEALTH_TTL_MS
    ) {
      setSetupHealth(healthCacheRef.current.data);
      return healthCacheRef.current.data;
    }
    try {
      const { data } = await api.get('/api/companion/setup-health');
      const health = data?.data || null;
      healthCacheRef.current = { at: now, companyId, data: health };
      setSetupHealth(health);
      // Keep HelperSidebar / OnboardingContext in sync with Guide (same next_action SSOT).
      try {
        window.dispatchEvent(new CustomEvent('waysen_setup_health_updated', {
          detail: {
            next_action: health?.next_action || null,
            setup_progress: health?.commerce_progress
              || (typeof health?.overall_percent === 'number'
                ? { percent: health.overall_percent }
                : null),
            overall_percent: health?.overall_percent,
          },
        }));
      } catch (_) { /* ignore */ }
      return health;
    } catch {
      setDegraded(true);
      return null;
    }
  }, [companyId]);

  const resolveLocalScene = useCallback((list, pathname, health, dismissed, completedFlows) => (
    resolveCompanionScene(list, pathname, health, dismissed, completedFlows)
  ), []);

  const refreshScene = useCallback(async () => {
    const list = await fetchScenes();
    const health = await fetchHealth();
    const pathWithSearch = `${location.pathname}${location.search || ''}`;
    const scene = resolveLocalScene(
      list,
      pathWithSearch,
      health,
      state.dismissed_scene_ids,
      state.completed_flow_ids
    );
    setActiveScene(scene);
    if (scene?.type === 'flow' && scene.blockNavigation === true) {
      setActiveFlowBlock((prev) => (prev?.id === scene.id ? prev : scene));
      // Soft: open floating window, do not force-reset user's talk/support mode
      setPanelState((p) => (p === 'minimized' || p === 'hidden' ? 'window' : p));
    } else if (activeFlowBlock && scene?.id !== activeFlowBlock.id) {
      setActiveFlowBlock(null);
    }
    return scene;
  }, [
    fetchScenes,
    fetchHealth,
    location.pathname,
    location.search,
    resolveLocalScene,
    state.dismissed_scene_ids,
    state.completed_flow_ids,
    activeFlowBlock,
  ]);

  useEffect(() => {
    if (!visible) return undefined;
    fetchState();
    refreshScene();
    refreshOperatorConsent();
    return undefined;
  }, [visible, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!visible) return;
    refreshScene();
  }, [location.pathname, location.search, visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Company switch invalidate
  useEffect(() => {
    invalidateCaches();
    setOperatorConsent(null);
    setOperatorActions([]);
    if (visible) {
      fetchState();
      refreshScene();
      refreshOperatorConsent();
    }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefetch consent + action catalog when opening Do / Tasks tabs
  useEffect(() => {
    if (!visible || (activeMode !== 'do' && activeMode !== 'tasks')) return;
    refreshOperatorConsent();
    refreshOperatorActions();
  }, [activeMode, visible, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!visible || (activeMode !== 'do' && activeMode !== 'tasks')) return;
    if (operatorConsent?.accepted === true) {
      refreshOperatorActions();
    }
  }, [operatorConsent?.accepted, activeMode, visible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onRefresh = () => {
      invalidateCaches();
      refreshScene();
    };
    window.addEventListener('mb-company-context-refreshed', onRefresh);
    return () => window.removeEventListener('mb-company-context-refreshed', onRefresh);
  }, [invalidateCaches, refreshScene]);

  // Hub «Обновить» / status load → invalidate companion health cache so sidebar/Guide
  // do not keep stale next_action (e.g. «SHOP → Платежи» after payment already ok).
  useEffect(() => {
    const onHub = (e) => {
      const d = e?.detail;
      if (!d) return;
      healthCacheRef.current = { at: 0, companyId: null, data: null };
      if (d.next_action || d.setup_progress) {
        setSetupHealth((prev) => ({
          ...(prev || {}),
          next_action: d.next_action != null ? d.next_action : prev?.next_action,
          commerce_progress: d.setup_progress || prev?.commerce_progress,
          overall_percent: typeof d.setup_progress?.percent === 'number'
            ? d.setup_progress.percent
            : prev?.overall_percent,
        }));
      }
      fetchHealth(true).catch(() => null);
    };
    window.addEventListener('commerce_hub_status_updated', onHub);
    return () => window.removeEventListener('commerce_hub_status_updated', onHub);
  }, [fetchHealth]);

  const patchState = useCallback(async (partial) => {
    setState((prev) => {
      const next = { ...prev, ...partial };
      if (partial.window_geometry) saveGeom(partial.window_geometry);
      return next;
    });
    try {
      await api.patch('/api/companion/state', partial);
    } catch {
      setDegraded(true);
    }
  }, []);

  const openPanel = useCallback((mode = 'guide', asWindow = true) => {
    if (state.visibility === 'off') return;
    setActiveMode(mode);
    setPanelState(resolveOpenPanelState(asWindow));
  }, [state.visibility, setActiveMode]);

  useEffect(() => {
    const onOpen = (ev) => {
      const mode = ev?.detail?.mode || 'guide';
      // Default floating window; explicit asWindow:false keeps dock
      const asWindow = ev?.detail?.asWindow !== false;
      openPanel(mode, asWindow);
    };
    /** Legacy FAB removed — support chat opens Companion mode «support». */
    const onSupport = () => openPanel('support', true);
    window.addEventListener('open_waysen_companion', onOpen);
    window.addEventListener('open_support_chat', onSupport);
    return () => {
      window.removeEventListener('open_waysen_companion', onOpen);
      window.removeEventListener('open_support_chat', onSupport);
    };
  }, [openPanel]);

  const minimize = useCallback(() => {
    setPanelState('minimized');
    setAvatarState('idle');
  }, []);

  const hide = useCallback(() => {
    setPanelState('hidden');
  }, []);

  const dismissScene = useCallback(async (sceneId) => {
    if (!sceneId) return;
    const next = Array.from(new Set([...(state.dismissed_scene_ids || []), sceneId]));
    const completed = Array.from(new Set([...(state.completed_flow_ids || [])]));
    const scene = scenes.find((s) => s.id === sceneId) || activeScene;
    if (scene && (scene.type === 'first_visit' || scene.type === 'flow') && !completed.includes(sceneId)) {
      completed.push(sceneId);
    }
    await patchState({
      dismissed_scene_ids: next,
      completed_flow_ids: completed,
    });
    if (activeFlowBlock?.id === sceneId) setActiveFlowBlock(null);
    setActiveScene(null);
    minimize();
  }, [state.dismissed_scene_ids, state.completed_flow_ids, patchState, minimize, scenes, activeScene, activeFlowBlock]);

  const completeFlow = useCallback(async (flowId) => {
    if (!flowId) return;
    const completed = Array.from(new Set([...(state.completed_flow_ids || []), flowId]));
    await patchState({ completed_flow_ids: completed });
    if (activeFlowBlock?.id === flowId) setActiveFlowBlock(null);
  }, [state.completed_flow_ids, patchState, activeFlowBlock]);

  // Soft blockNavigation: keep companion open as window; do not force-reset activeMode
  useEffect(() => {
    if (!activeFlowBlock || activeFlowBlock.blockNavigation !== true) return undefined;
    const prefixes = activeFlowBlock.routePrefixes || [];
    const onFlowRoute = prefixes.some((p) => location.pathname === p || location.pathname.startsWith(p));
    if (onFlowRoute && panelState === 'minimized') {
      setPanelState('window');
    }
    return undefined;
  }, [activeFlowBlock, location.pathname, panelState]);

  const openForm = useCallback(async (formId, prefill = null) => {
    if (!formId) return;
    setActiveMode('do');
    setActiveFormId(formId);
    setFormPrefill(prefill && typeof prefill === 'object' ? prefill : null);
    setPanelState((p) => (p === 'minimized' || p === 'hidden' ? 'window' : p));
    try {
      const { data } = await api.get(`/api/companion/forms/${formId}`);
      setFormSchema(data?.data || null);
    } catch {
      setFormSchema(null);
      setDegraded(true);
    }
  }, [setActiveMode]);

  /** Close inline Do form → empty «Сделать сейчас»; stay on mode do. */
  const closeForm = useCallback(() => {
    setActiveFormId(null);
    setFormSchema(null);
    setFormPrefill(null);
    setActiveMode('do');
  }, [setActiveMode]);

  const runAction = useCallback(async (action) => {
    if (!action) return;
    if (action.type === 'navigate' && action.path) {
      companionNavigate(navigate, action.path);
      return;
    }
    if (action.type === 'open_form' && action.form_id) {
      await openForm(action.form_id, action.prefill || null);
      return;
    }
    if (action.type === 'mode' && action.mode) {
      if (action.mode === 'do' && action.form_id) {
        await openForm(action.form_id, action.prefill || null);
        return;
      }
      setActiveMode(action.mode);
      setPanelState((p) => (p === 'minimized' || p === 'hidden' ? 'window' : p));
      return;
    }
    if (action.type === 'open_scene' && action.scene_id) {
      const scene = scenes.find((s) => s.id === action.scene_id);
      if (scene) {
        setActiveScene(scene);
        setActiveMode('guide');
        setPanelState((p) => (p === 'minimized' || p === 'hidden' ? 'window' : p));
      }
    }
  }, [navigate, openForm, scenes, setActiveMode]);

  const sendChat = useCallback(async (text, opts = {}) => {
    const images = Array.isArray(opts?.images)
      ? opts.images.filter((u) => typeof u === 'string' && u.startsWith('data:image/')).slice(0, MAX_PENDING_IMAGES)
      : [];
    const message = String(text || '').trim();
    if ((!message && !images.length) || loading || chatRequestRef.current
      || chatScopeRef.current.generation !== chatScopeKey) return false;
    const request = { generation: chatScopeKey };
    chatSendSequenceRef.current += 1;
    chatRequestRef.current = request;
    const isCurrentRequest = () => chatMountedRef.current
      && chatScopeRef.current.generation === request.generation
      && chatRequestRef.current === request;
    clearTimeout(chatAvatarTimerRef.current);
    setChatError(null);
    setActiveMode('talk');
    setPanelState((p) => (p === 'minimized' || p === 'hidden' ? 'window' : p));
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: message || (images.length ? '📷 Изображение' : ''),
        images: images.length ? images : undefined,
      },
    ]);
    setLoading(true);
    setAvatarState('thinking');
    try {
      const { data } = await api.post('/api/companion/chat', {
        message: message || (images.length ? 'Что на этом изображении? Кратко опиши и подскажи по Markbase.' : ''),
        images,
        mode: 'talk',
        session_id: sessionId,
        pathname: location.pathname,
        context: {
          moduleKey,
          setup_percent: setupHealth?.overall_percent,
          next_action: setupHealth?.next_action,
          persona: 'waysen',
          channel: 'platform_talk',
          read_only: true,
          image_urls: images,
        },
      });
      if (!isCurrentRequest()) return false;
      const payload = data?.data || {};
      if (data?.success !== true
        || typeof payload.reply !== 'string'
        || !payload.reply.trim()
        || payload.source !== 'waygpt'
        || payload.degraded !== false) {
        throw new Error('Companion response is not a confirmed answer');
      }
      if (payload.session_id) setSessionId(payload.session_id);
      setAvatarState(payload.avatar_state || 'speaking');
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: payload.reply,
          actions: payload.actions || [],
          read_only: payload.read_only !== false,
          channel: payload.channel || 'platform_talk',
        },
      ]);
      // Completion belongs to the provider, even if the sending window was
      // replaced by Dock or navigation while the request was in flight.
      setChatDraft('');
      setChatPendingImages([]);
      chatAvatarTimerRef.current = setTimeout(() => {
        if (chatMountedRef.current && chatScopeRef.current.generation === request.generation) {
          setAvatarState('idle');
        }
      }, 1200);
      return true;
    } catch {
      if (!isCurrentRequest()) return false;
      setChatError('Не удалось получить ответ.');
      setAvatarState('idle');
      return false;
    } finally {
      if (isCurrentRequest()) {
        chatRequestRef.current = null;
        setLoading(false);
      }
    }
  }, [sessionId, location.pathname, moduleKey, setupHealth, setActiveMode, loading, chatScopeKey]);

  const submitForm = useCallback(async (values) => {
    if (!activeFormId) return null;
    setLoading(true);
    try {
      const { data } = await api.post('/api/companion/action/submit', {
        form_id: activeFormId,
        values,
      });
      if (data?.success === false) {
        return { error: data?.error || 'Не удалось сохранить' };
      }
      const result = data?.data;
      setActiveFormId(null);
      setFormSchema(null);
      // Stay in Operator Do so Receipt is visible (do not bounce to Guide)
      setActiveMode('do');
      invalidateCaches();
      await fetchHealth(true);
      return result;
    } catch (e) {
      const code = e?.response?.data?.code;
      if (code === 'OPERATOR_CONSENT_REQUIRED') {
        await refreshOperatorConsent();
      }
      setDegraded(true);
      return {
        error: e?.response?.data?.error || 'Не удалось отправить. Попробуйте на странице мастера.',
        code,
      };
    } finally {
      setLoading(false);
    }
  }, [activeFormId, invalidateCaches, fetchHealth, refreshOperatorConsent, setActiveMode]);

  // Non-intrusive: default minimized; rare proactive expand only if auto + incomplete
  useEffect(() => {
    if (!visible || state.visibility !== 'auto' || state.only_on_demand) return;
    if (panelState !== 'minimized') return;
    const health = setupHealth;
    if (!health || health.overall_percent >= 100) return;
    const day = new Date().toISOString().slice(0, 10);
    if (proactiveRef.current.day !== day) {
      proactiveRef.current = { day, count: 0 };
    }
    if (proactiveRef.current.count >= 2) return;
    // Do not auto-expand — only ensure chip attention via avatar warn once
    if (health.next_action) {
      setAvatarState('warn');
      proactiveRef.current.count += 1;
      const t = setTimeout(() => setAvatarState('idle'), 4000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [visible, state.visibility, state.only_on_demand, setupHealth, panelState]);

  const value = useMemo(() => ({
    visible,
    panelState,
    setPanelState,
    activeMode,
    setActiveMode,
    avatarState,
    setAvatarState,
    state,
    patchState,
    scenes,
    activeScene,
    setupHealth,
    messages,
    setMessages,
    sessionId,
    activeFormId,
    formSchema,
    formPrefill,
    degraded,
    chatError,
    chatScopeKey,
    chatDraft,
    setChatDraft,
    chatPendingImages,
    setChatPendingImages,
    addChatFiles,
    loading,
    moduleKey,
    openPanel,
    minimize,
    hide,
    dismissScene,
    completeFlow,
    openForm,
    closeForm,
    runAction,
    sendChat,
    submitForm,
    refreshScene,
    fetchHealth,
    openWindow: () => openPanel(activeMode || 'talk', true),
    activeFlowBlock,
    operatorConsent,
    operatorConsentLoading,
    refreshOperatorConsent,
    acceptOperatorConsent,
    operatorActions,
    operatorActionsLoading,
    refreshOperatorActions,
  }), [
    visible, panelState, activeMode, setActiveMode, avatarState, state, patchState, scenes, activeScene,
    setupHealth, messages, sessionId, activeFormId, formSchema, formPrefill, degraded, chatError, chatScopeKey,
    chatDraft, chatPendingImages, addChatFiles, loading, moduleKey,
    openPanel, minimize, hide, dismissScene, completeFlow, openForm, closeForm, runAction, sendChat, submitForm,
    refreshScene, fetchHealth, activeFlowBlock,
    operatorConsent, operatorConsentLoading, refreshOperatorConsent, acceptOperatorConsent,
    operatorActions, operatorActionsLoading, refreshOperatorActions,
  ]);

  return (
    <WaySenCompanionContext.Provider value={value}>
      {children}
    </WaySenCompanionContext.Provider>
  );
}

export function useWaySenCompanion() {
  const ctx = useContext(WaySenCompanionContext);
  if (!ctx) {
    throw new Error('useWaySenCompanion must be used within WaySenCompanionProvider');
  }
  return ctx;
}

export function useWaySenCompanionOptional() {
  return useContext(WaySenCompanionContext);
}

export default WaySenCompanionContext;
